"use client";

import { useState } from "react";
import Link from "next/link";

interface BMIResult {
  value: number;
  category: string;
  color: string;
  recommendation: string;
  healthRisk: string;
}

const BMI_CATEGORIES = [
  {
    name: "Underweight",
    color: "#3B82F6",
    min: 0,
    max: 18.5,
    recommendation:
      "Consider consulting a healthcare professional for healthy weight gain strategies.",
    healthRisk: "Low",
  },
  {
    name: "Healthy Weight",
    color: "#10B981",
    min: 18.5,
    max: 25,
    recommendation:
      "Maintain your current healthy lifestyle with balanced nutrition and regular exercise.",
    healthRisk: "Minimal",
  },
  {
    name: "Overweight",
    color: "#F59E0B",
    min: 25,
    max: 30,
    recommendation:
      "Medical weight loss treatment can help you achieve a healthier weight safely.",
    healthRisk: "Moderate",
  },
  {
    name: "Obese Class I",
    color: "#F97316",
    min: 30,
    max: 35,
    recommendation:
      "Professional medical intervention is recommended for sustainable weight loss.",
    healthRisk: "High",
  },
  {
    name: "Obese Class II",
    color: "#EF4444",
    min: 35,
    max: 50,
    recommendation:
      "Urgent medical consultation recommended for comprehensive weight management.",
    healthRisk: "Very High",
  },
];

export default function BMICalculatorSection() {
  const [formData, setFormData] = useState({
    gender1: "",
    gender2: "",
    weightValue: "",
    weightUnit: "KG",
    heightValue: "",
    heightUnit: "CM",
    heightFeet: "",
    heightInches: "",
    weightStones: "",
    weightPounds: "",
  });

  const [bmiResult, setBMIResult] = useState<BMIResult>({
    value: 20.1,
    category: "Healthy Weight",
    color: "#10B981",
    recommendation:
      "Maintain your current healthy lifestyle with balanced nutrition and regular exercise.",
    healthRisk: "Minimal",
  });

  const calculateBMI = () => {
    // Check if we have weight and appropriate height data
    const hasWeight =
      formData.weightUnit === "ST/LB"
        ? formData.weightStones && formData.weightPounds !== ""
        : formData.weightValue;
    const hasHeight =
      formData.heightUnit === "CM"
        ? formData.heightValue
        : formData.heightFeet && formData.heightInches !== "";

    if (!hasWeight || !hasHeight) return;

    let weight = 0;
    let height = 0;

    // Convert weight to kg
    if (formData.weightUnit === "KG") {
      weight = parseFloat(formData.weightValue);
    } else if (formData.weightUnit === "LB") {
      weight = parseFloat(formData.weightValue) * 0.453592;
    } else if (formData.weightUnit === "ST/LB") {
      const stones = parseFloat(formData.weightStones) || 0;
      const pounds = parseFloat(formData.weightPounds) || 0;
      const totalPounds = stones * 14 + pounds; // 1 stone = 14 pounds
      weight = totalPounds * 0.453592; // convert total pounds to kg
    }

    // Convert height to meters
    if (formData.heightUnit === "CM") {
      height = parseFloat(formData.heightValue) / 100; // cm to meters
    } else if (formData.heightUnit === "FT/IN") {
      const feet = parseFloat(formData.heightFeet) || 0;
      const inches = parseFloat(formData.heightInches) || 0;
      const totalInches = feet * 12 + inches;
      height = totalInches * 0.0254; // inches to meters
    }

    const bmi = weight / (height * height);
    const category =
      BMI_CATEGORIES.find((cat) => bmi >= cat.min && bmi < cat.max) ||
      BMI_CATEGORIES[BMI_CATEGORIES.length - 1];

    setBMIResult({
      value: Math.round(bmi * 10) / 10,
      category: category.name,
      color: category.color,
      recommendation: category.recommendation,
      healthRisk: category.healthRisk,
    });
  };

  return (
    <section className="relative py-16 sm:py-20 bg-white overflow-hidden">
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-20 2xl:px-8 relative">
        {/* Premium Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2.5 bg-white/80 backdrop-blur-sm px-5 py-2.5 rounded-full mb-8 border border-gray-200/60 shadow-sm">
            <div className="w-2 h-2 bg-[#7771B8] rounded-full animate-pulse shadow-sm shadow-[#7771B8]/50"></div>
            <span className="text-gray-700 font-semibold text-sm tracking-wide">
              Health Assessment
            </span>
          </div>

          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-semibold text-gray-900 mb-6 leading-[1.1] font-[family-name:var(--font-playfair)]">
            Discover your{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#7771B8] via-[#9d97d8] to-[#7771B8]">
              health status
            </span>
          </h2>

          <p className="text-lg sm:text-xl text-gray-500 max-w-2xl mx-auto leading-relaxed font-light">
            Get an instant assessment of your current health status with our
            advanced BMI calculator, designed by medical professionals.
          </p>
        </div>

        {/* Premium Calculator Card */}
        <div className="bg-white rounded-2xl shadow-[0_4px_32px_rgba(0,0,0,0.06)] border border-gray-100/80 p-8 lg:p-12 mb-16">
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-12 lg:gap-16 items-stretch">
            {/* Left: Calculator Form */}
            <div className="flex flex-col space-y-6">
              {/* Section Header */}
              <div className="space-y-4">
                <h3 className="text-xl lg:text-2xl font-semibold text-gray-900 font-[family-name:var(--font-playfair)]">
                  Calculate your BMI
                </h3>
                <p className="text-gray-500 leading-relaxed font-light">
                  Enter your details below for a comprehensive health assessment
                </p>
              </div>

              {/* Premium Form - Flexible to fill space */}
              <div className="flex-1 flex flex-col justify-between space-y-6">
                {/* Gender Selection */}
                <div>
                  <label className="block text-sm font-bold text-gray-800 mb-3">
                    Select your gender *
                  </label>
                  <select
                    className="w-full p-4 rounded-lg border-2 border-gray-200 focus:border-[#7771B8] focus:ring-0 text-gray-900 bg-white shadow-sm transition-all duration-200 hover:border-gray-300"
                    value={formData.gender2}
                    onChange={(e) =>
                      setFormData({ ...formData, gender2: e.target.value })
                    }
                  >
                    <option value="" className="text-gray-500">
                      Select Gender
                    </option>
                    <option value="male" className="text-gray-900">
                      Male
                    </option>
                    <option value="female" className="text-gray-900">
                      Female
                    </option>
                  </select>
                </div>

                {/* Weight & Height Section */}
                <div className="grid grid-cols-1 gap-6 bg-gradient-to-r from-gray-50/80 to-white/80 p-6 rounded-xl border border-gray-100 flex-1">
                  {/* Weight Input */}
                  <div className="space-y-4">
                    <label className="block text-sm font-bold text-gray-800">
                      Enter your weight *
                    </label>

                    {/* Unit Selection */}
                    <div className="grid grid-cols-3 gap-2">
                      {["KG", "LB", "ST/LB"].map((unit) => (
                        <button
                          key={unit}
                          type="button"
                          className={`px-3 py-2 rounded-lg text-sm font-semibold border-2 transition-all duration-200 ${
                            formData.weightUnit === unit
                              ? "bg-[#7771B8] border-[#7771B8] text-white shadow-lg"
                              : "bg-white border-gray-200 text-gray-600 hover:border-[#7771B8]/50 hover:bg-gray-50"
                          }`}
                          onClick={() =>
                            setFormData({
                              ...formData,
                              weightUnit: unit,
                              weightValue: "",
                              weightStones: "",
                              weightPounds: "",
                            })
                          }
                        >
                          {unit}
                        </button>
                      ))}
                    </div>

                    {formData.weightUnit === "KG" ? (
                      <input
                        type="number"
                        placeholder="Enter weight in kg"
                        className="w-full p-4 rounded-lg border-2 border-gray-200 focus:border-[#7771B8] focus:ring-0 text-gray-900 bg-white shadow-sm transition-all duration-200 placeholder-gray-400"
                        value={formData.weightValue}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            weightValue: e.target.value,
                          })
                        }
                      />
                    ) : formData.weightUnit === "LB" ? (
                      <input
                        type="number"
                        placeholder="Enter weight in pounds"
                        className="w-full p-4 rounded-lg border-2 border-gray-200 focus:border-[#7771B8] focus:ring-0 text-gray-900 bg-white shadow-sm transition-all duration-200 placeholder-gray-400"
                        value={formData.weightValue}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            weightValue: e.target.value,
                          })
                        }
                      />
                    ) : (
                      <div className="grid grid-cols-2 gap-2">
                        <input
                          type="number"
                          placeholder="Stones"
                          min="0"
                          max="50"
                          className="w-full p-4 rounded-lg border-2 border-gray-200 focus:border-[#7771B8] focus:ring-0 text-gray-900 bg-white shadow-sm transition-all duration-200 placeholder-gray-400"
                          value={formData.weightStones || ""}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              weightStones: e.target.value,
                            })
                          }
                        />
                        <input
                          type="number"
                          placeholder="Pounds"
                          min="0"
                          max="13"
                          className="w-full p-4 rounded-lg border-2 border-gray-200 focus:border-[#7771B8] focus:ring-0 text-gray-900 bg-white shadow-sm transition-all duration-200 placeholder-gray-400"
                          value={formData.weightPounds || ""}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              weightPounds: e.target.value,
                            })
                          }
                        />
                      </div>
                    )}
                  </div>

                  {/* Height Input */}
                  <div className="space-y-4">
                    <label className="block text-sm font-bold text-gray-800">
                      Enter your height *
                    </label>

                    {/* Unit Selection */}
                    <div className="grid grid-cols-2 gap-2">
                      {["CM", "FT/IN"].map((unit) => (
                        <button
                          key={unit}
                          type="button"
                          className={`px-4 py-2 rounded-lg text-sm font-semibold border-2 transition-all duration-200 ${
                            formData.heightUnit === unit
                              ? "bg-[#7771B8] border-[#7771B8] text-white shadow-lg"
                              : "bg-white border-gray-200 text-gray-600 hover:border-[#7771B8]/50 hover:bg-gray-50"
                          }`}
                          onClick={() =>
                            setFormData({
                              ...formData,
                              heightUnit: unit,
                              heightValue: "",
                              heightFeet: "",
                              heightInches: "",
                            })
                          }
                        >
                          {unit}
                        </button>
                      ))}
                    </div>

                    {formData.heightUnit === "CM" ? (
                      <input
                        type="number"
                        placeholder="Enter height in cm"
                        className="w-full p-4 rounded-lg border-2 border-gray-200 focus:border-[#7771B8] focus:ring-0 text-gray-900 bg-white shadow-sm transition-all duration-200 placeholder-gray-400"
                        value={formData.heightValue}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            heightValue: e.target.value,
                          })
                        }
                      />
                    ) : (
                      <div className="grid grid-cols-2 gap-2">
                        <input
                          type="number"
                          placeholder="Feet"
                          min="0"
                          max="8"
                          className="w-full p-4 rounded-lg border-2 border-gray-200 focus:border-[#7771B8] focus:ring-0 text-gray-900 bg-white shadow-sm transition-all duration-200 placeholder-gray-400"
                          value={formData.heightFeet || ""}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              heightFeet: e.target.value,
                            })
                          }
                        />
                        <input
                          type="number"
                          placeholder="Inches"
                          min="0"
                          max="11"
                          className="w-full p-4 rounded-lg border-2 border-gray-200 focus:border-[#7771B8] focus:ring-0 text-gray-900 bg-white shadow-sm transition-all duration-200 placeholder-gray-400"
                          value={formData.heightInches || ""}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              heightInches: e.target.value,
                            })
                          }
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* Premium Calculate Button */}
                <button
                  onClick={calculateBMI}
                  className="group w-full bg-gradient-to-r from-[#7771B8] to-purple-500 hover:from-[#6860a3] hover:to-purple-600 text-white py-3.5 px-8 rounded-lg font-semibold text-base shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 mt-auto"
                >
                  <span className="flex items-center justify-center space-x-2">
                    <span>Calculate my BMI</span>
                    <svg
                      className="w-5 h-5 transform group-hover:translate-x-1 transition-transform duration-200"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </span>
                </button>
              </div>
            </div>

            {/* Right: BMI Result Display */}
            <div className="flex flex-col space-y-6">
              {/* BMI Result Display */}
              <div className="text-center space-y-8">
                {/* Accurate BMI Gauge - Built with precise math */}
                <div className="relative w-72 h-40 mx-auto">
                  <svg
                    width="100%"
                    height="100%"
                    viewBox="0 0 280 160"
                    className="overflow-visible"
                  >
                    {/* Background Arc */}
                    <path
                      d="M 20 140 A 120 120 0 0 1 260 140"
                      fill="none"
                      stroke="#E5E7EB"
                      strokeWidth="16"
                    />

                    {/* BMI Range Arcs - Mathematically calculated */}
                    {BMI_CATEGORIES.map((category, _) => {
                      const startAngle = (category.min / 40) * 180; // 0-180 degrees
                      const endAngle = Math.min((category.max / 40) * 180, 180);

                      const centerX = 140;
                      const centerY = 140;
                      const radius = 120;

                      // Calculate start and end points
                      const startX =
                        centerX -
                        radius * Math.cos((startAngle * Math.PI) / 180);
                      const startY =
                        centerY -
                        radius * Math.sin((startAngle * Math.PI) / 180);
                      const endX =
                        centerX - radius * Math.cos((endAngle * Math.PI) / 180);
                      const endY =
                        centerY - radius * Math.sin((endAngle * Math.PI) / 180);

                      const largeArcFlag = endAngle - startAngle > 90 ? 1 : 0;

                      return (
                        <path
                          key={category.name}
                          d={`M ${startX} ${startY} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${endX} ${endY}`}
                          fill="none"
                          stroke={category.color}
                          strokeWidth="12"
                        />
                      );
                    })}

                    {/* Needle - Precisely calculated */}
                    <g
                      transform={`translate(140, 140) rotate(${
                        (bmiResult.value / 40) * 180 - 180
                      })`}
                    >
                      <line
                        x1="0"
                        y1="0"
                        x2="85"
                        y2="0"
                        stroke="#1F2937"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                      <circle cx="0" cy="0" r="4" fill="#1F2937" />
                      <circle cx="0" cy="0" r="2" fill="#FFFFFF" />
                    </g>

                    {/* Scale markers */}
                    {[0, 18.5, 25, 30, 35, 40].map((value) => {
                      const angle = (value / 40) * 180;
                      const centerX = 140;
                      const centerY = 140;
                      const outerRadius = 115;
                      const innerRadius = 105;

                      const x1 =
                        centerX -
                        outerRadius * Math.cos((angle * Math.PI) / 180);
                      const y1 =
                        centerY -
                        outerRadius * Math.sin((angle * Math.PI) / 180);
                      const x2 =
                        centerX -
                        innerRadius * Math.cos((angle * Math.PI) / 180);
                      const y2 =
                        centerY -
                        innerRadius * Math.sin((angle * Math.PI) / 180);

                      return (
                        <line
                          key={value}
                          x1={x1}
                          y1={y1}
                          x2={x2}
                          y2={y2}
                          stroke="#6B7280"
                          strokeWidth="2"
                        />
                      );
                    })}
                  </svg>

                  {/* BMI Display */}
                  <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-8">
                    <div className="text-center">
                      <div className="text-3xl font-black text-gray-900">
                        {bmiResult.value}
                      </div>
                    </div>
                  </div>
                </div>

                {/* BMI Category Indicator */}
                <div className="inline-flex items-center space-x-3 bg-gray-50 px-6 py-3 rounded-lg border border-gray-200 mt-6">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: bmiResult.color }}
                  ></div>
                  <span className="text-sm font-semibold text-gray-700">
                    {bmiResult.category}
                  </span>
                </div>
              </div>

              {/* Professional Recommendation */}
              <div className="bg-gradient-to-r from-gray-50/80 to-white/80 p-6 rounded-xl border border-gray-100 shadow-sm">
                <h4 className="text-base font-bold text-gray-900 mb-3 flex items-center">
                  <svg
                    className="w-5 h-5 text-[#7771B8] mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014.846 17H9.154a3.374 3.374 0 00-2.969-1.46l-.548-.547z"
                    />
                  </svg>
                  Medical recommendation
                </h4>
                <p className="text-gray-700 leading-relaxed text-sm">
                  {bmiResult.recommendation}
                </p>
              </div>

              {/* Elegant Legend */}
              <div className="grid grid-cols-1 gap-3">
                <h5 className="text-sm font-bold text-gray-800 mb-2">
                  BMI categories
                </h5>
                {BMI_CATEGORIES.map((category) => (
                  <div
                    key={category.name}
                    className="flex items-center justify-between p-3 bg-white/50 rounded-lg border border-gray-100 shadow-sm"
                  >
                    <div className="flex items-center space-x-3">
                      <div
                        className="w-4 h-4 rounded shadow-sm"
                        style={{ backgroundColor: category.color }}
                      ></div>
                      <span className="text-sm font-medium text-gray-800">
                        {category.name}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 font-medium">
                      {category.min} - {category.max}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Premium Action Section */}
        <div className="mt-16 text-center">
          <div className="bg-gradient-to-br from-gray-50/80 via-white to-purple-50/30 p-10 lg:p-14 rounded-2xl shadow-[0_4px_32px_rgba(0,0,0,0.06)] border border-gray-100/80 relative overflow-hidden">
            {/* Subtle decorative elements */}
            <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-bl from-[#7771B8]/5 to-transparent rounded-full blur-2xl"></div>
            <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-tr from-purple-100/50 to-transparent rounded-full blur-2xl"></div>

            <div className="max-w-3xl mx-auto relative">
              <h3 className="text-2xl lg:text-3xl font-semibold text-gray-900 mb-4 font-[family-name:var(--font-playfair)]">
                Ready to transform your health?
              </h3>
              <p className="text-lg text-gray-500 mb-8 leading-relaxed font-light">
                Take the next step towards your ideal weight with our
                medically-supervised programme. Professional guidance every step
                of the way.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  href="/consultation/GUHMJBNGE33R7CJ"
                  className="group relative overflow-hidden bg-gradient-to-r from-[#7771B8] via-[#8679c4] to-[#7771B8] bg-size-200 bg-pos-0 hover:bg-pos-100 text-white px-8 py-4 rounded-xl font-semibold shadow-lg shadow-[#7771B8]/20 hover:shadow-xl hover:shadow-[#7771B8]/30 transition-all duration-500 hover:scale-[1.02] inline-block text-center"
                >
                  <span className="relative z-10">
                    Start medical consultation
                  </span>
                  <div className="absolute inset-0 bg-gradient-to-r from-[#6860a3] to-purple-600 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                </Link>

                <Link
                  href="/about"
                  className="group text-center text-gray-500 hover:text-[#7771B8] font-medium transition-all duration-300 px-8 py-4"
                >
                  <span className="flex items-center justify-center gap-1.5">
                    Learn about our programme
                    <svg
                      className="w-4 h-4 transform group-hover:translate-x-0.5 transition-transform duration-300"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </span>
                </Link>
              </div>

              {/* Trust Indicators */}
              <div className="flex flex-wrap justify-center items-center gap-6 mt-8 pt-6 border-t border-gray-200">
                <div className="text-gray-400 text-sm flex items-center">
                  <svg
                    className="w-4 h-4 mr-1.5 text-emerald-500"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  CQC Regulated
                </div>
                <div className="text-gray-400 text-sm flex items-center">
                  <svg
                    className="w-4 h-4 mr-1.5 text-emerald-500"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  MHRA Licensed
                </div>
                <div className="text-gray-400 text-sm flex items-center">
                  <svg
                    className="w-4 h-4 mr-1.5 text-emerald-500"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  NMC Registered
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
